import React, { useEffect, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { Search } from "lucide-react";
import { FaEdit, FaEye, FaTrashAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/ConfirmModal";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import ScaleLoader from "react-spinners/ScaleLoader";

const BankType = () => {
  const navigate = useNavigate();

  /* ---------------- STATE ---------------- */
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const limit = 9999999;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /* ---------------- FETCH BANK LIST ---------------- */
  const fetchBanks = async () => {
    setLoading(true);

    const body = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      page,
      limit,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.bankList,
        JSON.stringify(body)
      );

      if (res?.result) {
        setBanks(res.data || []);
      } else {
        setBanks([]);
        toast.error(res?.message || "Failed to load bank list");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, [page]);

  /* ---------------- DELETE ---------------- */
  const handleDeleteIncome = async () => {
    setIsDeleting(true);

    const body = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      id: deleteId,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.bankDelete,
        JSON.stringify(body)
      );

      if (res?.result) {
        toast.success(res.message);
        fetchBanks();
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="income-type-container">
      {/* Header */}
      <div className="special-header panel-white-all">
        <h4 className="page-title">Bank Details</h4>
        <div className="action-buttons-group">
          <Button className="btn btn-primary" onClick={() => navigate("/accountant/CreateBank")}>
            Create Bank Details
          </Button>
          <Button className="btn btn-primary">Export</Button>
        </div>
      </div>

      <div className="yc-list-body">
        {/* Column Header */}
        <div className="yc-list-thead-card">
          <div className="yc-list-grid yc-list-grid-4">
            <div className="yc-list-cell">Sr.No</div>
            <div className="yc-list-cell">Bank Name</div>
            <div className="yc-list-cell">Account Holder Name</div>
            <div className="yc-list-cell">Actions</div>
          </div>
        </div>

        {/* Rows */}
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "20vh" }}>
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : banks.length === 0 ? (
          <div className="yc-empty-state">No data found</div>
        ) : (
          <div className="yc-list-row-list">
            {banks.map((bank, index) => (
              <div key={bank.bank_id} className="yc-list-row-card">
                <div className="yc-list-grid yc-list-grid-4">
                  <div className="yc-list-cell">{index + 1}</div>
                  <div className="yc-list-cell">{bank.bank_name}</div>
                  <div className="yc-list-cell">{bank.bank_account_holder_name}</div>
                  <div className="yc-list-cell yc-list-cell-actions">
                    <button
                      className="yc-action-icon"
                      title="View"
                      onClick={() => navigate(`/accountant/ViewBank/${bank.bank_id}`)}
                    >
                      <FaEye />
                    </button>
                    <button
                      className="yc-action-icon"
                      title="Edit"
                      onClick={() => navigate(`/accountant/EditBank/${bank.bank_id}`)}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="yc-action-icon yc-action-icon--danger"
                      title="Delete"
                      onClick={() => {
                        setDeleteId(bank.bank_id);
                        setShowDeleteModal(true);
                      }}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* DELETE CONFIRM MODAL */}
      <ConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        title="Delete Income"
        message="Are you sure you want to delete this income record?"
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDeleteIncome}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default BankType;
